import type { ReleaseApprovalMode } from '@src/dto';
import { cn } from '../../../lib/utils';
import { FieldLabel } from '../../Form/FieldLabel';
import { StatusPill } from '../../portal';
import type { DappFormInputChangeEvent } from '../types';

// Temporarily hide the selector while preserving existing form values.
const SHOW_RELEASE_APPROVAL_MODE = false;

const OPTIONS: {
  value: ReleaseApprovalMode;
  label: string;
  description: string;
  isDefault?: boolean;
}[] = [
  {
    value: 'Automatic',
    label: 'Publish automatically',
    description:
      'Goes live in the dApp Store as soon as it is approved. Nothing more to do.',
    isDefault: true,
  },
  {
    value: 'Manual',
    label: 'Wait for me to publish',
    description:
      'Approved releases wait in Releases until you publish them. Best for timing a launch.',
  },
];

interface ReleaseApprovalModeFieldProps {
  value: ReleaseApprovalMode;
  onChange?: (event: DappFormInputChangeEvent) => void;
}

/**
 * "When a release is approved" option cards (Figma 13327:27903) shared by the
 * Store Listing and New dApp forms. Native radios (visually hidden) drive
 * `Form.Root`; the card border/fill reflects the checked state.
 */
export function ReleaseApprovalModeField({
  value,
  onChange,
}: ReleaseApprovalModeFieldProps) {
  if (!SHOW_RELEASE_APPROVAL_MODE) {
    return <input type="hidden" name="releaseApprovalMode" value={value} />;
  }

  return (
    // Inline padding reset: the unlayered normalize in index.css pads fieldsets
    // and beats Tailwind's layered preflight.
    <fieldset className="flex flex-col gap-2" style={{ padding: 0 }}>
      <legend className="mb-2 p-0">
        <FieldLabel
          label="When a release is approved"
          required
          explanation="Choose what happens after Solana Mobile approves a release."
        />
      </legend>
      {OPTIONS.map((option) => {
        const isChecked = value === option.value;
        const titleId = `release-approval-${option.value}-title`;
        const descriptionId = `release-approval-${option.value}-description`;
        return (
          <label
            key={option.value}
            className={cn(
              'group flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-4 transition-colors duration-150',
              isChecked
                ? 'border-white bg-seeker-800'
                : 'border-seeker-700 hover:border-seeker-500',
            )}
          >
            <input
              type="radio"
              name="releaseApprovalMode"
              value={option.value}
              aria-labelledby={titleId}
              aria-describedby={descriptionId}
              checked={isChecked}
              onChange={onChange}
              className="peer sr-only"
            />
            <span
              aria-hidden="true"
              className={cn(
                'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors duration-150',
                isChecked
                  ? 'border-white'
                  : 'border-seeker-500 group-hover:border-seeker-300',
                'peer-focus-visible:ring-2 peer-focus-visible:ring-white/70',
              )}
            >
              <span
                className={cn(
                  'size-2 rounded-full bg-white transition-opacity duration-150',
                  isChecked ? 'opacity-100' : 'opacity-0',
                )}
              />
            </span>
            <span className="flex min-w-0 flex-col gap-1">
              <span className="flex flex-wrap items-center gap-2 text-sm font-bold leading-5 text-white">
                <span id={titleId}>{option.label}</span>
                {option.isDefault ? (
                  <StatusPill tone="default">Default</StatusPill>
                ) : null}
              </span>
              <span
                id={descriptionId}
                className="text-xs leading-4 text-seeker-400"
              >
                {option.description}
              </span>
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}
